import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A component that shows/hides installation date/time inputs
 * based on the selected variant.
 */
export class ProductInstallationOptions extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const section = this.closest('.shopify-section, dialog');

    // Listen for variant changes
    section?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );

    // Run initial check on load
    this.#checkCurrentVariant();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles variant updates.
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant) return;
    this.#toggleVisibility(variant);
  };

  /**
   * Checks the initially selected variant on load.
   */
  #checkCurrentVariant() {
    const currentVariantData = document.querySelector('[data-product-json]');
    if (!currentVariantData) return;

    try {
      const product = JSON.parse(currentVariantData.textContent);
      const selected = product?.variants?.find(
        (v) => v.id === product?.selected_variant_id
      );

      if (selected) this.#toggleVisibility(selected);
    } catch (err) {
      console.warn('Failed to read product JSON:', err);
    }
  }

  /**
   * Toggles visibility based on whether variant includes "with installation".
   * Also resets all inputs when installation is not selected.
   */
  #toggleVisibility(variant) {
    const hasInstallation = variant.options.some((opt) =>
      opt.toLowerCase().includes('with installation')
    );

    this.style.display = hasInstallation ? 'block' : 'none';

    if (!hasInstallation) {
      this.#resetAllInputs();
    }
  }

  /**
   * Resets ALL inputs inside the custom element.
   * Future-proof: works for any new fields added later.
   */
  #resetAllInputs() {
    const elements = this.querySelectorAll('input, select, textarea');

    elements.forEach((el) => {
      switch (el.type) {
        case 'checkbox':
        case 'radio':
          el.checked = false;
          break;

        default:
          el.value = '';
          break;
      }

      // Trigger events so Shopify syncs line-item properties correctly
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

if (!customElements.get('product-installation-options')) {
  customElements.define(
    'product-installation-options',
    ProductInstallationOptions
  );
}

if (!customElements.get('product-installation-options')) {
  customElements.define('product-installation-options', ProductInstallationOptions);
}
